'use client';

/**
 * ConversationArea — scrollable message container for the V2 chat.
 *
 * Built from scratch using use-stick-to-bottom for auto-scroll behavior.
 * Replaces the V1 ConversationView — no pb-32 padding needed because the
 * input bar lives inside the CSS Grid, not fixed-positioned.
 *
 * Exposes ChatLayoutRef for backward compatibility with ChatContext's
 * chatLayoutRef (used by WebSocket handler for imperative scroll).
 *
 * Scroll-up lazy-loads older history pages (useHistoryPagination), with scroll-
 * anchor preservation so prepending older messages doesn't jump the viewport.
 */

import React, {
  useImperativeHandle,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { StickToBottom, useStickToBottomContext } from 'use-stick-to-bottom';
import { Button } from '@/components/ui/button';
import { ArrowDownIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MessageRole, type ChatLayoutRef } from '@/types/chat';
import { useHistoryPagination } from '@/hooks/useHistoryPagination';
import { useChatContext } from '@/contexts/ChatContext';

// ── Types ─────────────────────────────────────────────────────────

interface ConversationAreaProps {
  children: React.ReactNode;
  className?: string;
}

// ── Scroll-to-bottom button (lives inside StickToBottom context) ──

function ScrollToBottomButton() {
  const { isAtBottom, scrollToBottom } = useStickToBottomContext();

  if (isAtBottom) return null;

  return (
    <div className="absolute inset-x-0 bottom-2 flex justify-center">
      <Button
        variant="outline"
        size="icon"
        onClick={() => scrollToBottom()}
        className="size-8 rounded-full shadow-md"
        aria-label="Scroll to bottom"
      >
        <ArrowDownIcon className="size-4" />
      </Button>
    </div>
  );
}

// ── Scroll-up older-history loader (lives inside StickToBottom context) ──
//
// Renders at the TOP of the content. Detects scroll-up near the top, loads the
// next-older page, and preserves the scroll position so the viewport stays put
// when older messages prepend (anchor = scrollHeight captured before the load,
// re-applied once the message count grows).

const SCROLL_UP_THRESHOLD_PX = 64;

function ScrollUpLoader() {
  const { scrollRef } = useStickToBottomContext();
  const { hasMore, isLoading, error, loadOlder } = useHistoryPagination();
  const messageCount = useChatContext().messages.length;

  const anchorHeightRef = useRef<number | null>(null);
  const prevCountRef = useRef(messageCount);

  // After an older page prepends (count grew AND we had captured an anchor),
  // restore scrollTop by the height added above the viewport. Live appends (no
  // anchor captured) are ignored, so only scroll-up prepends adjust position.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (
      el &&
      anchorHeightRef.current != null &&
      messageCount > prevCountRef.current
    ) {
      el.scrollTop += el.scrollHeight - anchorHeightRef.current;
    }
    anchorHeightRef.current = null;
    prevCountRef.current = messageCount;
  }, [messageCount, scrollRef]);

  // Capture the anchor then load. Used by both auto-scroll-up and retry.
  const triggerLoad = useCallback(() => {
    const el = scrollRef.current;
    if (el) anchorHeightRef.current = el.scrollHeight;
    void loadOlder();
  }, [scrollRef, loadOlder]);

  // Detect scroll-up near the top → load the next-older page. Suppressed while
  // loading or after an error (the user retries explicitly via the button).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      if (
        el.scrollTop <= SCROLL_UP_THRESHOLD_PX &&
        hasMore &&
        !isLoading &&
        !error
      ) {
        triggerLoad();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [scrollRef, hasMore, isLoading, error, triggerLoad]);

  // Nothing more to load and no error → render nothing (genuinely at the top).
  if (!hasMore && !error) return null;

  return (
    <div className="flex justify-center py-2 text-xs text-[#7a7f96]">
      {isLoading && <span>Loading earlier messages…</span>}
      {!isLoading && error && (
        <button
          type="button"
          onClick={triggerLoad}
          className="rounded-full border-[1px] border-[#e0ddd9] px-[12px] py-1 transition-colors hover:bg-[#f2f0ee]"
        >
          Couldn’t load earlier messages — retry
        </button>
      )}
    </div>
  );
}

// ── New-turn top anchor (lives inside StickToBottom context) ──────
//
// FB27 #6 (owner decision: all screen sizes): when the user sends a message,
// the old behavior let use-stick-to-bottom follow the streaming reply to its
// BOTTOM — on phones the top of a long answer scrolled straight out of view.
// Instead, park the viewport so the just-sent user message sits at the top
// and the reply streams in below, read top-down.
//
// The anchor can't be applied at send time: the reply doesn't exist yet, so
// the anchor position IS the bottom (clamped). And it can't be held with raw
// scrollTop writes while the lib is bottom-locked — browser scroll events
// coalesce per frame, so the lib's follow animation simply overwrites them
// without ever "seeing" the upward jump (observed live). So the turn runs in
// two phases, using the lib's own state machine:
//   1. PENDING — scrollToBottom() re-engages the lib's native follow (also
//      covers a user who had scrolled up into history before sending): the
//      fresh bubble and early stream stay visible exactly as before.
//   2. ANCHOR — the moment the question's top becomes reachable (the stream
//      has outgrown the viewport), stopScroll() flips the lib's
//      escapedFromLock/isAtBottom switches — cancelling its follow animation
//      for the rest of the turn — and one scrollTop write pins the question
//      at the top; the reply keeps growing below the fold. The existing ↓
//      button jumps to the live tail, and scrolling back to the bottom
//      re-engages following (the lib's normal re-lock).

/** Breathing room above the anchored user bubble. */
const ANCHOR_TOP_OFFSET_PX = 8;

function AnchorNewTurn() {
  const { scrollRef, stopScroll, scrollToBottom } = useStickToBottomContext();
  const { messages } = useChatContext();
  // Adopted-without-scrolling on first non-empty render: a mount or history
  // restore must keep its scroll-to-bottom behavior, so only user messages
  // appended AFTER that anchor. null = nothing adopted yet.
  const anchoredIdRef = useRef<string | null>(null);
  const pendingIdRef = useRef<string | null>(null);
  const initializedRef = useRef(false);

  useLayoutEffect(() => {
    const last = messages[messages.length - 1];
    if (!initializedRef.current) {
      if (last?.role === MessageRole.USER) anchoredIdRef.current = last.id;
      initializedRef.current = messages.length > 0;
      return;
    }
    if (last?.role === MessageRole.USER && last.id !== anchoredIdRef.current) {
      anchoredIdRef.current = last.id;
      pendingIdRef.current = last.id;
      scrollToBottom();
    }

    const pendingId = pendingIdRef.current;
    if (!pendingId) return;
    const scroller = scrollRef.current;
    const node = scroller?.querySelector(
      `[data-message-id="${CSS.escape(pendingId)}"]`
    );
    if (!scroller || !node) {
      pendingIdRef.current = null;
      return;
    }
    const nodeTop =
      node.getBoundingClientRect().top -
      scroller.getBoundingClientRect().top +
      scroller.scrollTop;
    const target = Math.max(0, nodeTop - ANCHOR_TOP_OFFSET_PX);
    if (scroller.scrollHeight - scroller.clientHeight >= target) {
      stopScroll();
      scroller.scrollTop = target;
      pendingIdRef.current = null;
    }
  }, [messages, scrollRef, stopScroll, scrollToBottom]);

  return null;
}

// ── Component ─────────────────────────────────────────────────────

export const ConversationArea = React.forwardRef<
  ChatLayoutRef,
  ConversationAreaProps
>(function ConversationArea({ children, className }, ref) {
  // StickToBottom handles auto-scroll internally. scrollToBottom is exposed
  // for imperative calls from the WebSocket handler via chatLayoutRef.
  const scrollToBottom = useCallback(() => {
    // No-op: StickToBottom manages scroll automatically.
    // Kept for backward compatibility with ChatContext.chatLayoutRef API.
  }, []);

  useImperativeHandle(ref, () => ({ scrollToBottom }), [scrollToBottom]);

  return (
    <StickToBottom
      className={cn('relative flex-1 overflow-y-hidden', className)}
      initial="smooth"
      // resize MUST stay "instant": with "smooth", every streamed chunk starts
      // a follow animation that AnchorNewTurn then has to cancel/overwrite
      // (stopScroll + scrollTop writes) — a forced-reflow fight at the stream's
      // cadence, measured as the first-message "bounce" in the flicker trace.
      // Instant resize snaps the bottom-lock follow with no animation to fight;
      // initial="smooth" keeps the pleasant scroll on conversation load.
      resize="instant"
      role="log"
    >
      {/* max-w-4xl (was max-w-3xl) so bubbles + tool results breathe on
          desktop — the chat card itself is max-w-5xl, the previous
          max-w-3xl wasted ~256px of horizontal space. px-[16px] on mobile
          (was px-[20px]) reclaims another ~8px of content width. */}
      <StickToBottom.Content className="mx-auto flex w-full max-w-4xl flex-col gap-[20px] px-[16px] pb-6 pt-[16px] sm:px-6">
        <ScrollUpLoader />
        <AnchorNewTurn />
        {children}
      </StickToBottom.Content>
      <ScrollToBottomButton />
    </StickToBottom>
  );
});
